// src/hooks/useFeature.js — Feature Gate Hook v148
// Usage: const { can, gate, planId } = useFeature()
import { useApp } from '../context/AppContext';
import { usePremium } from './usePremium';
import { G, GD } from '../constants';

// ECONOMY: ONE product, five durations. The old four-tier naming (Pro /
// Pro / Pro / Pro) asked users to decide WHAT to buy AND for HOW LONG
// at the same time — every extra decision is a place someone gives up. Both successful
// competitors sell one thing at different durations.
export const PLAN_LABELS = {
  '1d':  'Pro · 3-Day Pass (₹29)',
  '1m':  'Pro · 1 Month (₹299)',
  '3m':  'Pro · 3 Months (₹799)',
  '6m':  'Pro · 6 Months (₹1,099)',
  '12m': 'Pro · 12 Months (₹1,499)',
};

// FEATURE_META: feature → [minPlan, displayName, upgradeMsg]
// minPlan = null → free feature
export const FEATURE_META = {
  // Trades & Portfolio
  unlimited_trades:      ['1d',  'Unlimited Trades',              'Upgrade to unlock unlimited daily trades'],
  no_ads:                ['1d',  'Ad-Free',                       'Upgrade to remove ads'],
  // Order Book
  order_book:            ['1d',  'Full Order Book',               'Upgrade to view the full order book'],
  // Price Alerts
  price_alerts:          ['1d',  'Price Alerts (10)',              'Upgrade for more price alerts'],
  price_alerts_50:       ['3m',  'Price Alerts (50)',              'Upgrade to Pro for 50 alerts'],
  price_alerts_unlimited:['12m', 'Unlimited Price Alerts',        'Upgrade to Pro for unlimited alerts'],
  // Watchlist
  watchlist_20:          ['1d',  'Watchlist (20)',                 'Upgrade for more watchlist coins'],
  watchlist_30:          ['1m',  'Watchlist (30)',                 'Upgrade to Pro for 30 watchlist slots'],
  watchlist_50:          ['3m',  'Watchlist (50)',                 'Upgrade to Pro for 50 watchlist slots'],
  watchlist_unlimited:   ['12m', 'Unlimited Watchlist',           'Upgrade to Pro for unlimited watchlist'],
  // CVD
  cvd_basic:             [null,  'CVD Basic',                      ''],
  cvd_advanced:          ['1m',  'CVD Advanced',                  'Upgrade to Pro for multi-TF CVD, divergence & order flow'],
  // Whale Tracker
  whale_basic:           ['1d',  'Whale Tracker',                 'Upgrade to track live whale movements'],
  whale_advanced:        ['1m',  'Whale Advanced',                'Upgrade to Pro for whale summary & alerts'],
  // S-053 (LOCKED — 2026-05-10) FPA fix: Granular whale tier keys per Option D spec.
  // Match backend FEATURE_RULES requires:[] arrays exactly.
  whale_aggregate:       ['1m',  'Whale 24h Aggregate',           'Upgrade to Pro for 24h whale aggregate summary'],
  whale_pressure:        ['1m',  'Whale Pressure Ratio',          'Upgrade to Pro for whale buy/sell pressure ratio'],
  whale_alerts:          ['1m',  'Whale Alerts',                  'Upgrade to Pro for whale alert subscriptions'],
  whale_historical:      ['3m',  'Whale Historical Charts',       'Upgrade to Pro for historical whale charts'],
  whale_wallet:          ['3m',  'Whale Wallet Tracking',         'Upgrade to Pro for whale wallet tracking'],
  // Heatmap
  heatmap_basic:         ['1d',  'Delta Heatmap',                 'Upgrade to view Delta Heatmap'],
  heatmap_advanced:      ['3m',  'Advanced Heatmap',              'Upgrade to Pro for full heatmap grid'],
  // S-054 (LOCKED — 2026-05-10) FPA fix: Granular heatmap tier keys per Option D spec.
  heatmap_multitf:       ['3m',  'Multi-Timeframe Heatmap',       'Upgrade to Pro for multi-timeframe heatmap (15m/1h/4h/1d)'],
  heatmap_all_coins:     ['3m',  'All-Coins Heatmap',             'Upgrade to Pro for all-50-coins heatmap coverage'],
  heatmap_sectors:       ['3m',  'Sector Heatmap',                'Upgrade to Pro for sector-grouped heatmap (L1/DeFi/Memes/AI)'],
  heatmap_pressure:      ['3m',  'Pressure Trend Overlay',        'Upgrade to Pro for pressure trend overlay'],
  heatmap_breakouts:     ['3m',  'Breakout Signals',              'Upgrade to Pro for breakout signal detection'],
  heatmap_alerts:        ['12m', 'Heatmap Sector Alerts',         'Upgrade to Pro for heatmap sector-move alerts'],
  // Market Movers
  market_movers:         ['1d',  'Market Movers & Screener',      'Upgrade to access Market Movers screener'],
  // News
  news_basic:            [null,  'News Headlines',                 ''],
  news_advanced:         ['1m',  'AI News Sentiment',             'Upgrade to Pro for AI news sentiment & trade bias'],
  // S-054 (LOCKED — 2026-05-10) FPA fix: Granular news tier keys per Option D Strip+Combinations.
  news_sentiment:        ['1d',  'News Sentiment + Impact',       'Upgrade to view news sentiment + impact'],
  news_ai_summary:       ['1m',  'AI News Summary + Bias',        'Upgrade to Pro for AI Summary + Trade Bias on news'],
  news_ai_commentary:    ['3m',  'AI News Commentary',            'Upgrade to Pro for multi-paragraph AI commentary on news'],
  news_alerts:           ['12m', 'News Alerts (Watched Coins)',   'Upgrade to Pro for news alerts on watched coins'],
  // Signal Engine
  signal_engine:         ['1d',  'Signal Engine',                 'Upgrade to access the AI Signal Engine'],
  signal_full:           ['3m',  'Full Signal Engine',            'Upgrade to Pro for all signal sources'],
  // AI
  ai_basic:              ['1d',  'Basic AI',                      'Upgrade for AI trading assistance'],
  ai_medium:             ['1m',  'AI Signals',                    'Upgrade to Pro for AI signals'],
  ai_advanced:           ['3m',  'Advanced AI',                   'Upgrade to Pro for advanced AI analysis'],
  ai_full:               ['12m', 'Full AI Suite',                 'Upgrade to Pro for the complete AI suite'],
  // Trading Features
  trailing_stop_loss:    ['1m',  'Trailing Stop Loss',            'Upgrade to Pro for trailing stop loss'],
  exit_all_positions:    ['1m',  'Exit All Positions',            'Upgrade to Pro for exit all positions at once'],
  // S-012-pre: Options + Futures available to ALL users (Sandeep's roadmap intent).
  // minPlan=null = free for all. Tier differentiation via max_leverage_50/100/125.
  futures_trading:       [null,  'Futures Trading',               ''],
  options_trading:       [null,  'Options Trading',               ''],
  max_leverage_50:       ['1m',  'Up to 50x Leverage',            'Upgrade to Pro for higher leverage'],
  max_leverage_100:      ['3m',  'Up to 100x Leverage',           'Upgrade to Pro for 100x leverage'],
  max_leverage_125:      ['12m', 'Up to 125x Leverage',           'Upgrade to Pro for maximum 125x leverage'],
  // Algo Trading
  algo_basic:            ['1m',  'Algo Trading (2 bots)',         'Upgrade to Pro for algo trading bots'],
  algo_advanced:         ['3m',  'Algo Trading (All 6 bots)',     'Upgrade to Pro for all 6 algo bots'],
  // Backtesting & Strategy
  backtesting:           ['3m',  'Backtesting Engine',            'Upgrade to Pro for strategy backtesting'],
  strategy_builder:      ['3m',  'Strategy Builder',              'Upgrade to Pro for the strategy builder'],
  // Copy Trading
  copy_trading:          ['3m',  'Copy Trading',                  'Upgrade to Pro for copy trading'],
  // Portfolio Analytics
  equity_curve:          ['1d',  'Equity Curve',                  'Upgrade to view your live equity curve'],
  pnl_export:            ['1m',  'PnL Export (PDF/CSV)',          'Upgrade to Pro for export PnL reports'],
  profit_calendar:       ['1m',  'Profit Calendar Heatmap',       'Upgrade to Pro for profit calendar heatmap'],
  // Leaderboard & Social
  leaderboard_country:   ['1d',  'Country Leaderboard',           'Upgrade to view country-specific rankings'],
  // S-048 (2026-05-10): `season_rewards` removed per Sandeep's locked decision.
  // Feature unadvertised; DB tables + /api/store/seasons/* endpoints preserved.
  forums_unlimited:      ['1m',  'Unlimited Forum Posts',         'Upgrade to Pro for unlimited forum posts'],
  // Labs & Clubs
  // v234: competition_entry removed — competitions are now FREE for ALL users (practice mode)
  clubs_create:          ['3m',  'Create Trading Clubs',          'Upgrade to Pro for create trading clubs'],
  // Education
  courses_basic:         [null,  'Basic Courses',                  ''],
  courses_advanced:      ['1m',  'Advanced Courses',              'Upgrade to Pro for advanced trading courses'],
  // Support
  priority_support:      ['12m', 'Priority Support',              'Upgrade to Pro for priority customer support'],
};

// PLAN_FEATURES: plan → array of features unlocked
export const PLAN_FEATURES = {
  // S-012-pre: Free users now get futures_trading + options_trading (Sandeep's roadmap intent).
  // Tier differentiation lives in max_leverage_50/100/125 — those stay tier-gated.
  free: new Set(['cvd_basic','news_basic','courses_basic','futures_trading','options_trading']),
  '1d': new Set([
    'unlimited_trades','no_ads','order_book','price_alerts','watchlist_20',
    'cvd_basic','whale_basic','heatmap_basic','market_movers','signal_engine',
    'ai_basic','news_basic','courses_basic','futures_trading','options_trading','equity_curve',
    'leaderboard_country',
    // S-054 FPA fix: 1d unlocks news_sentiment (sentiment + impact + tags)
    'news_sentiment',
  ]),
  '1m': new Set([
    'unlimited_trades','no_ads','order_book','price_alerts','watchlist_20','watchlist_30',
    'cvd_basic','cvd_advanced','whale_basic','whale_advanced','heatmap_basic',
    'market_movers','signal_engine','ai_basic','ai_medium','news_basic','news_advanced',
    'trailing_stop_loss','exit_all_positions','futures_trading','options_trading','max_leverage_50',
    'algo_basic','equity_curve','pnl_export','profit_calendar',
    'leaderboard_country','forums_unlimited',
    'courses_basic','courses_advanced',
    // S-053 FPA fix: 1m unlocks whale aggregate + pressure + alerts (5 max alerts)
    'whale_aggregate','whale_pressure','whale_alerts',
    // S-054 FPA fix: 1m unlocks news_sentiment + news_ai_summary
    'news_sentiment','news_ai_summary',
  ]),
  '3m': new Set([
    'unlimited_trades','no_ads','order_book','price_alerts','price_alerts_50',
    'watchlist_20','watchlist_30','watchlist_50',
    'cvd_basic','cvd_advanced','whale_basic','whale_advanced','heatmap_basic','heatmap_advanced',
    'market_movers','signal_engine','signal_full','ai_basic','ai_medium','ai_advanced',
    'news_basic','news_advanced',
    'trailing_stop_loss','exit_all_positions','futures_trading','options_trading',
    'max_leverage_50','max_leverage_100',
    'algo_basic','algo_advanced','backtesting','strategy_builder','copy_trading',
    'equity_curve','pnl_export','profit_calendar',
    'leaderboard_country','forums_unlimited','clubs_create',
    'courses_basic','courses_advanced',
    // S-053 FPA fix: 3m unlocks ALL whale features incl. historical + wallet (20 max alerts)
    'whale_aggregate','whale_pressure','whale_alerts','whale_historical','whale_wallet',
    // S-054 FPA fix: 3m unlocks all heatmap advanced (except alerts — 12m only)
    'heatmap_multitf','heatmap_all_coins','heatmap_sectors','heatmap_pressure','heatmap_breakouts',
    // S-054 FPA fix: 3m unlocks news_sentiment + news_ai_summary + news_ai_commentary
    'news_sentiment','news_ai_summary','news_ai_commentary',
  ]),
  '12m': new Set([
    'unlimited_trades','no_ads','order_book',
    'price_alerts','price_alerts_50','price_alerts_unlimited',
    'watchlist_20','watchlist_30','watchlist_50','watchlist_unlimited',
    'cvd_basic','cvd_advanced','whale_basic','whale_advanced','heatmap_basic','heatmap_advanced',
    'market_movers','signal_engine','signal_full','ai_basic','ai_medium','ai_advanced','ai_full',
    'news_basic','news_advanced',
    'trailing_stop_loss','exit_all_positions','futures_trading','options_trading',
    'max_leverage_50','max_leverage_100','max_leverage_125',
    'algo_basic','algo_advanced','backtesting','strategy_builder','copy_trading',
    'equity_curve','pnl_export','profit_calendar',
    'leaderboard_country','forums_unlimited','clubs_create',
    'courses_basic','courses_advanced','priority_support',
    // S-053 FPA fix: 12m unlocks all whale (UNLIMITED alerts via whale_alerts_limit:-1)
    'whale_aggregate','whale_pressure','whale_alerts','whale_historical','whale_wallet',
    // S-054 FPA fix: 12m unlocks ALL heatmap incl. alerts
    'heatmap_multitf','heatmap_all_coins','heatmap_sectors','heatmap_pressure','heatmap_breakouts','heatmap_alerts',
    // S-054 FPA fix: 12m unlocks ALL news incl. alerts
    'news_sentiment','news_ai_summary','news_ai_commentary','news_alerts',
  ]),
};

// Watchlist limits per plan
// ECONOMY: Pro is unlimited on everything. The old 10/20/30/50/∞ ladders were the SAME
// capability sold four times — watchlist_20, watchlist_30, watchlist_50,
// watchlist_unlimited were one feature wearing four hats.
const _UNL = Infinity;
const WATCHLIST_LIMITS = { free:10, '1d':_UNL, '1m':_UNL, '3m':_UNL, '6m':_UNL, '12m':_UNL };
const ALERT_LIMITS     = { free:3,  '1d':_UNL, '1m':_UNL, '3m':_UNL, '6m':_UNL, '12m':_UNL };
const FORUMS_LIMITS    = { free:3,  '1d':_UNL, '1m':_UNL, '3m':_UNL, '6m':_UNL, '12m':_UNL };
// Leverage stays tiered by nothing but free-vs-Pro: free is 1x (5x for one trade via a
// rewarded ad on mobile), Pro is full. Kept as a map so callers don't change.
const MAX_LEVERAGE     = { free:1,  '1d':125, '1m':125, '3m':125, '6m':125, '12m':125 };

export function useFeature() {
  const { user, setPage } = useApp();
  // P-018: Use canonical usePremium hook (Rule H — single source). Removed
  // dependency on `useApp().isPremium` which was duplicate-defined in
  // AppContext with a stricter check (`user.plan && user.plan !== 'free'`).
  // Achieves webapp/mobile parity — both platforms now use usePremium for
  // isPremium derivation.
  const { isPremium } = usePremium();
  // v275 (G4.22a — Doubt #69a): Backend source-of-truth is `user.plan` (∈ {'free','1d','1m','3m','12m'}),
  // NOT `user.subscription_plan` which doesn't exist on user object. Previous code silently
  // downgraded ALL paid users (3m/12m) to '1m' tier limits. Fixing here propagates correctly to
  // 10+ webapp pages: AlertsPage, BacktesterTab, CoursesTab, AlgoToolsTab, StrategyBuilderTab,
  // SignalEnginePage, WatchlistPage, CopyTradingPage, NewsPage, TopMoversPage, ChartPage, ProfilePage.
  // 'premium' literal kept in fallback chain for legacy compatibility (never matched in practice).
  const planId = user?.plan || (isPremium ? '1m' : 'free');
  const allowed = PLAN_FEATURES[planId] || PLAN_FEATURES.free;

  // ECONOMY: ONE TIER. Any paid plan unlocks every gated feature.
  //
  // The 48 entries in FEATURE_META are kept as-is deliberately: they still carry the display
  // name and upgrade copy for each feature, which the gate() prompt needs. Only the ACCESS
  // decision collapses. Doing it here rather than rewriting PLAN_FEATURES means a future
  // feature is automatically Pro-gated by adding one FEATURE_META row — there is no second
  // list to keep in sync, and no way to accidentally leave a feature unreachable on a tier.
  const isPaid = planId && planId !== 'free';
  const can = (feature) => {
    const meta = FEATURE_META[feature];
    if (!meta || meta[0] === null) return true;  // free feature
    if (isPaid) return true;                      // Pro unlocks everything
    return allowed.has(feature);
  };

  const gate = (feature, component, compact = false) => {
    if (can(feature)) return component;
    const meta = FEATURE_META[feature] || ['1m', feature, 'Upgrade to unlock'];
    return <UpgradePrompt feature={meta[1]} message={meta[2]} minPlan={meta[0]} compact={compact} setPage={setPage}/>;
  };

  const alertLimit     = () => ALERT_LIMITS[planId]     ?? 3;
  const watchlistLimit = () => WATCHLIST_LIMITS[planId] ?? 10;
  const forumsLimit    = () => FORUMS_LIMITS[planId]    ?? 3;
  const maxLeverage    = () => MAX_LEVERAGE[planId]     ?? 1;

  return { can, gate, planId, allowed, alertLimit, watchlistLimit, forumsLimit, maxLeverage, FEATURE_META, PLAN_LABELS };
}

// ── Upgrade Prompt Component ───────────────────────────────────────────────────
function UpgradePrompt({ feature, message, minPlan, compact, setPage }) {
  // P-CTA-TIER-ROUTING (2026-05-20): Set preselect sessionStorage (S-017 pattern)
  // so PremiumPage opens with the correct tier pre-selected. Previously, just
  // setPage('premium') → page defaulted to '1m' (Pro), even if user
  // needed e.g. '3m' (Pro) for the feature they were trying to unlock.
  // ECONOMY: one product — there is no "which tier unlocks this" any more. Every paid
  // duration unlocks everything, so the prompt always opens the plan page at the default
  // (monthly) rather than pre-selecting a higher tier the user does not need.
  const validTiers = ['1d', '1m', '3m', '6m', '12m'];
  const goUpgrade = () => {
    try {
      if (validTiers.includes(minPlan)) {
        sessionStorage.setItem('pc_premium_preselect', minPlan);
      }
    } catch {}
    setPage('premium');
  };

  if (compact) {
    return (
      <div onClick={goUpgrade}
        style={{ display:'inline-flex', alignItems:'center', gap:5, cursor:'pointer',
          background:'#F5C51820', border:'1px solid #F5C51844',
          borderRadius:20, padding:'4px 12px', fontSize:11, color:'#F5C518', fontWeight:700 }}>
        ⭐ {PLAN_LABELS[minPlan]?.split(' ')[0]} required
      </div>
    );
  }
  return (
    <div style={{ textAlign:'center', padding:'30px 20px',
      background:'linear-gradient(135deg,#1A1200,#0D0A00)',
      border:`1px solid ${G}33`, borderRadius:14, margin:'8px 0' }}>
      <div style={{ fontSize:38, marginBottom:8 }}>🔒</div>
      <div style={{ fontWeight:900, color:G, fontSize:16, marginBottom:6 }}>{feature}</div>
      <div style={{ color:'#888', fontSize:12, marginBottom:16, lineHeight:1.5 }}>{message}</div>
      <button onClick={goUpgrade}
        style={{ padding:'10px 28px', borderRadius:20, border:'none',
          background:`linear-gradient(135deg,${GD},${G})`,
          color:'#000', fontWeight:800, fontSize:14, cursor:'pointer' }}>
        ⭐ Upgrade — {PLAN_LABELS[minPlan]}
      </button>
    </div>
  );
}

export { UpgradePrompt };
